import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";

import BreadCrumb from "../../../Components/Common/BreadCrumb";
import TableContainer from "../../../Components/Common/TableContainer";
import Loader from "../../../Components/Common/Loader";
import SupplierEditView from "./SupplierAddView";
import ImportExcelModal from "../../../Components/Common/ImportExcelModal";
import {
  useDeleteSupplierByIdMutation,
  useGetSuppliersQuery,
} from "slices/InventoryManagement/inventoryManagementSlice";
import { IsValidPermissions } from "Routes/AuthProtected";

interface Supplier {
  id: number;
  name: string;
  registration_number?: string | null;
  tax_id?: string | null;
  contact_name?: string | null;
  contact_email: string;
  contact_phone?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  postal_code?: string | null;
  website?: string | null;
  business_type?: string | null;
  credit_term?: string | null;
  credit_limit?: number | null;
  currency_code?: string | null;
  tax?: string | null;
  created_at: string;
  modified_at?: string | null;
}

const SuppliersList: React.FC = () => {
  const navigate = useNavigate();

  // local UI state
  const [data, setData] = useState<Supplier[]>([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentSupplierId, setCurrentSupplierId] = useState<number | null>(
    null
  );
  const [actionDropdownOpen, setActionDropdownOpen] = useState<{
    [key: number]: boolean;
  }>({});
  const [isImportExcel, setIsImportExcel] = useState<boolean>(false);
  const [pageState, setPageState] = useState({ page: 1, pageSize: 30, search: "" });

  // List hook with pagination + search
  const {
    data: suppliersResponse,
    isLoading: loadingSuppliers,
    refetch: refetchSuppliers,
  } = useGetSuppliersQuery(pageState);
  const [deleteSupplierById] = useDeleteSupplierByIdMutation();

  // keep local copy sorted by id ascending
  useEffect(() => {
    if (suppliersResponse?.results) {
      const sorted = [...suppliersResponse.results].sort(
        (a, b) => Number(a.id) - Number(b.id)
      );
      setData(sorted);
    } else {
      setData([]);
    }
  }, [suppliersResponse]);

  // handlers for edit/create modal
  const openCreateModal = () => {
    setCurrentSupplierId(null);
    setIsEditMode(false);
    setShowEditModal(true);
  };

  const openEditModal = (id: number) => {
    setCurrentSupplierId(id);
    setIsEditMode(true);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setCurrentSupplierId(null);
    setIsEditMode(false);
  };

  const handleSaved = () => {
    // close edit modal and refresh list
    closeEditModal();
    refetchSuppliers();
  };

  // handler for view page navigation
  const handleViewSupplier = (id: number) => {
    navigate(`/purchase-supplierdetail/${id}`);
  };

  const handleDeleteSupplier = async (supplier: Supplier) => {
    if (!window.confirm(`Delete supplier "${supplier.name}"? This cannot be undone.`)) return;
    try {
      await deleteSupplierById(supplier.id).unwrap();
      toast.success("Supplier deleted successfully.");
      refetchSuppliers();
    } catch (error: any) {
      toast.error(error?.data?.detail || error?.data?.error || "Failed to delete supplier.");
    }
  };

  // toggle action dropdown for specific row
  const toggleActionDropdown = (id: number) => {
    setActionDropdownOpen((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const baseColumns = useMemo(
    () => [
      // { Header: "ID", accessor: "id" },
      {
        Header: "Supplier Name",
        accessor: "name",
        Cell: (row: any) => (
          <span className="text-body">{row.row.original.name}</span>
        ),
      },
      {
        Header: "Contact Name",
        accessor: "contact_name",
        Cell: (cell: any) =>
          cell.row.original.contact_name ? (
            <span>{cell.row.original.contact_name}</span>
          ) : (
            <span className="text-muted">—</span>
          ),
      },
      {
        Header: "Email",
        accessor: "contact_email",
        Cell: (cell: any) => (
          <a href={`mailto:${cell.row.original.contact_email}`}>
            {cell.row.original.contact_email}
          </a>
        ),
      },
      {
        Header: "Phone",
        accessor: "contact_phone",
        Cell: (cell: any) =>
          cell.row.original.contact_phone ? (
            <span>{cell.row.original.contact_phone}</span>
          ) : (
            <span className="text-muted">—</span>
          ),
      },
      {
        Header: "Address",
        accessor: "address",
        Cell: (cell: any) =>
          cell.row.original.address ? (
            <span>{cell.row.original.address}</span>
          ) : (
            <span className="text-muted">—</span>
          ),
      },
      {
        Header: "Business Type",
        accessor: "business_type",
        Cell: (cell: any) =>
          cell.row.original.business_type ? (
            <span>{cell.row.original.business_type}</span>
          ) : (
            <span className="text-muted">—</span>
          ),
      },
      {
        Header: "Created",
        accessor: "created_at",
        Cell: (cell: any) =>
          new Date(cell.row.original.created_at).toLocaleDateString(),
      },
      // {
      //   Header: "Modified",
      //   accessor: "modified_at",
      //   Cell: (cell: any) =>
      //     cell.row.original.modified_at
      //       ? new Date(cell.row.original.modified_at).toLocaleDateString()
      //       : "-",
      // },
      {
        Header: "Action",
        Cell: (cellProps: any) => {
          const s: Supplier = cellProps.row.original;
          const isOpen = actionDropdownOpen[s.id] || false;

          return (
            <div className="d-flex gap-2">
              {/* <Button
                color="primary"
                size="sm"
                onClick={() => handleViewSupplier(s.id)}
                className="btn btn-soft-primary btn-sm"
              >
                <i className="ri-eye-fill align-bottom me-1" />
                View
              </Button> */}

              <Dropdown
                isOpen={isOpen}
                toggle={() => toggleActionDropdown(s.id)}
                direction="start"
              >
                <DropdownToggle
                  color="light"
                  size="sm"
                  className="btn btn-soft-secondary btn-sm dropdown-toggle"
                >
                  <i className="ri-more-2-fill align-bottom" />
                </DropdownToggle>
                <DropdownMenu>
                  {IsValidPermissions(["EDIT_INVENTORY_SUPPLIERS"]) &&
                    <DropdownItem onClick={() => openEditModal(s.id)}>
                      <i className="ri-pencil-line align-bottom me-2 text-muted" />
                      Edit
                    </DropdownItem>
                  }
                  {IsValidPermissions(["EDIT_INVENTORY_SUPPLIERS"]) &&
                    <DropdownItem className="text-danger" onClick={() => handleDeleteSupplier(s)}>
                      <i className="ri-delete-bin-line align-bottom me-2" />
                      Delete
                    </DropdownItem>
                  }
                  {(IsValidPermissions(["VIEW_ONLY_INVENTORY_SUPPLIERS"]) || IsValidPermissions(["VIEW_ALL_INVENTORY_SUPPLIERS"])) &&
                    <DropdownItem onClick={() => handleViewSupplier(s.id)}>
                      <i className="ri-eye-line align-bottom me-2 text-muted" />
                      View Details
                    </DropdownItem>
                  }
                </DropdownMenu>
              </Dropdown>
            </div>
          );
        },
      },
    ],
    [actionDropdownOpen]
  );

  // Quick fix: mark columns non-sortable by default (so header has no sort toggle)
  const columns = useMemo(
    () => baseColumns.map((c) => ({ ...c, disableSortBy: true })),
    [baseColumns]
  );

  document.title = "Suppliers | Resource Inventory";

  if (loadingSuppliers) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Suppliers" pageTitle="Resource Inventory" />
          <div className="text-center py-5">
            <Loader />
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Suppliers" pageTitle="Resource Inventory" />

        <ImportExcelModal
          show={isImportExcel}
          onCloseClick={() => setIsImportExcel(false)}
          import_type={"inventory_supplier_import"}
          refetch={refetchSuppliers}
        />

        <Row>
          <Col xl={12}>
            <Card>
              <div className="card-header border-0 d-flex align-items-center justify-content-between">
                <h5 className="mb-0">Suppliers</h5>

                <div className="d-flex gap-2">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Search suppliers..."
                    value={pageState.search}
                    onChange={(e) => {
                      setPageState((prev) => ({ ...prev, search: e.target.value, page: 1 }));
                    }}
                    style={{ maxWidth: 260 }}
                  />
                  {IsValidPermissions(["ADD_INVENTORY_SUPPLIERS"]) && (
                    <>
                      <Button color="primary" size="sm" onClick={openCreateModal}>
                        <i className="ri-add-line align-bottom me-1" />
                        Add
                      </Button>
                      <Button
                        color="secondary"
                        size="sm"
                        onClick={() => setIsImportExcel(true)}
                      >
                        <i className="ri-file-download-line align-bottom me-1"></i>
                        Import
                      </Button>
                    </>
                  )}
                </div>
              </div>

              <div className="card-body pt-0">
                {data && data.length > 0 ? (
                  <TableContainer
                    columns={columns}
                    data={data}
                    isGlobalFilter={false}
                    customPageSize={pageState.pageSize}
                    divClass="table-responsive mb-1"
                    tableClass="mb-0 align-middle table-borderless"
                    theadClass="table-light text-muted"
                    SearchPlaceholder="Search Suppliers..."
                    isPagination={false}
                    isServerPagination
                    serverPaginationProps={suppliersResponse}
                    setPageState={setPageState}
                  />
                ) : (
                  <div className="py-4 text-center">
                    <div>
                      <i className="ri-search-line display-5 text-success" />
                    </div>
                    <div className="mt-4">
                      <h5>Sorry! No Suppliers Found</h5>
                      <p className="text-muted">
                        Click "Add Supplier" to create one.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </Col>
        </Row>

        {/* Edit/Create Modal - Only for inline editing from list */}
        <Modal isOpen={showEditModal} toggle={closeEditModal} size="lg">
          <ModalHeader toggle={closeEditModal}>
            {isEditMode ? "Edit Supplier" : "Create Supplier"}
          </ModalHeader>
          <ModalBody>
            <SupplierEditView
              supplierId={currentSupplierId ?? undefined}
              compact={true}
              onSaved={handleSaved}
              onCancel={closeEditModal}
            />
          </ModalBody>
        </Modal>
      </Container>
    </div>
  );
};

export default SuppliersList;
